import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, HostListener, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog, MatSlideToggleChange } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'angularx-social-login';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { WorkflowDataService } from 'src/@core/services/job/workflow-data.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { TwilioService } from 'src/@core/services/twilio.service';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { WorkflowPopupComponent } from '../workflow-popup/workflow-popup.component';
import { CallSummaryPopupComponent } from './call-summary-popup/call-summary-popup.component';
import { EditTasksPopupComponent } from './edit-tasks-popup/edit-tasks-popup.component';
import { SignalDetailPopupComponent } from './signal-detail-popup/signal-detail-popup.component';
import { EditOutcomePopupComponent } from './edit-outcome-popup/edit-outcome-popup.component';
import { ClientProfileComponent } from '../view-project-new/client-profile/client-profile.component';
import { RunOutreachPopupComponent } from '../view-project-new/run-outreach-popup/run-outreach-popup.component';
import { CreateTaskDialogComponent } from '../entity-leads/create-task-dialog/create-task-dialog.component';
import { DeleteFollowUpTasksDialogComponent } from './delete-followup-tasks-dialog/delete-followup-tasks-dialog.component';
import { PostCallSignal, SignalStrength } from '../entity-leads/models/post-call-signal.types';
import {
  extractPostCallSignals,
  resolveLeadTaskPrompt,
  titleFromTaskPrompt,
  signalStrengthClass,
} from '../entity-leads/utils/post-call-signals.util';
import logoLinkedIn from '@iconify/icons-logos/linkedin';
import logoGitHub from '@iconify/icons-logos/github';
import logoTwitter from '@iconify/icons-logos/twitter';
import logoFacebook from '@iconify/icons-logos/facebook';
import { CspSafeAudioPlayer, getFetchAudioErrorMessage } from 'src/@core/utils/audio-playback.util';
import { getWhatsAppDocumentsErrorMessage, openPresignedDownload } from 'src/@core/utils/presigned-download.util';
import { OutreachActivityEvent, OutreachActivityService } from 'src/@core/services/outreach-activity.service';
// Root-provided, so this is a plain service import rather than a module dependency.
// Reusing it keeps one definition of the report metrics endpoint and its types.
import { ReportsService } from 'src/app/modules/reports/reports.service';

/** The outcome a lead is stamped with when it opens a campaign link. */
const LINK_CLICKED_STATUS = 'Link Clicked';

/** Projects spell that outcome their own way — "Link clicked", "link Clicked". */
function isLinkClickedStatus(status: string): boolean {
  return typeof status === 'string'
    && status.trim().toLowerCase() === LINK_CLICKED_STATUS.toLowerCase();
}

interface TimeframeOption {
  label: string;
  value: string;
}

/** A resolved timeframe. Both dates, or neither — the API rejects half a range. */
interface DateRange {
  startDate?: string;
  endDate?: string;
}

@Component({
  selector: 'hour4u-admin-connected-leads',
  templateUrl: './connected-leads.component.html',
  styleUrls: ['./connected-leads.component.scss']
})
export class ConnectedLeadsComponent implements OnInit {

  @Input() callData?: any = null;
  @Input() refreshTrigger: number;
  @Input() projectDetails: any;
  @Input() pageIndex?: number = 1;
  //@Input() pageSize?: number = 10000;
  @Input() pageSizeOptions?: number[] = [10, 20, 50, 100];
  @Input() currentRange?: string = '';
  @Input() hideCallButton: boolean = false;
  @Output() pageChange = new EventEmitter<{ pageNum: number; pageSize: number }>();
  @Input() showWorkflowStatus = false;

  selectedLeads = 0;
  processedLeads = 0;

  selection = new SelectionModel<any>(true, []);
  dataSource: any[] = []; // Initialize as empty array
  allRowsSelected: boolean = false;
  profile = this.appService.getCurrentAccount();
  private intervalId: any;
  private callHistoryIntervalId: any;
  /** Bound for in-progress workflow status only (not continuous table refresh). */
  private readonly workflowPollMs = 15000;
  private readonly workflowPollMaxTicks = 40; // ~10 min max, then stop
  private workflowPollTicks = 0;
  diameter = 40;
  loaderColor: string;
  mode = 'indeterminate';
  isLoading: boolean = true; // Start with loading state
  isRefreshing = false;
  currentAudio: CspSafeAudioPlayer | null = null;
  playingRecordingId: string | null = null;
  // pageIndex = 1;
  pageSize = 10;
  pagination: any;
  // currentRange: string = '';
  // pageSizeOptions: number[] = [10, 20, 50, 100, 200, 500];
  isExporting: boolean = false;
  isDownloadingProjectWaDocs = false;
  downloadingWaDocsClientNumber: string | null = null;
  currentRecordingUrl: string | null = null;
  metadata: any;

  logoLinkedIn = logoLinkedIn;
  logoGitHub = logoGitHub;
  logoTwitter = logoTwitter;
  logoFacebook = logoFacebook;

  showEmploymentModal = false;
  selectedEmploymentHistory: any[] = [];
  workflowPopupData: any[];
  projectId: any;

  expectedOutcomes: any[] = [];
  process: any;
  requiredLeads: any;
  workflowPaused: boolean = false;

  streamEvents: OutreachActivityEvent[] = [];
  streamPaused = false;
  wsConnected = false;
  showActivityStream = false;
  private streamSub: Subscription | null = null;
  private wsConnectedSub: Subscription | null = null;
  private readonly compactLogLimit = 20;

  @ViewChild('activityLogScroll', { static: false }) activityLogScroll: ElementRef;

  // Filter properties
  selectedStatusFilter: string[] = [];
  // Set when the Unique Link Clicks card is active. Mutually exclusive with
  // selectedStatusFilter: one asks "at this status now", the other "ever".
  observedStatusFilter: string | null = null;
  reviewedByHumanFilter = false;
  statusFilterOptions: string[] = [];
  /** Filter Connected rows by latest job signal.strength (comma-joined to API). */
  selectedSignalStrengthFilter: SignalStrength[] = [];
  signalStrengthFilterOptions: SignalStrength[] = [
    'Strong',
    'Good',
    'Weak',
    'No signal',
    'Excluded',
  ];
  nameFilter = '';
  mobileFilter = '';
  durationFilter = '';
  nameFilterDraft = '';
  mobileFilterDraft = '';
  durationFilterDraft = '';
  activeFilterPanel: 'name' | 'mobile' | 'duration' | 'status' | 'signal' | null = null;
  filterPanelPosition: { [key: string]: string } = {};
  private readonly filterPanelWidth = 260;
  leadsStatusCount: any;
  reviewedByHumanTotalCount = 0;

  // Timeframe filter. The option list mirrors the Reports page so the two read
  // the same way, and 'all' is the default so the screen loads as it always has.
  timeframeOptions: TimeframeOption[] = [
    { label: 'All time', value: 'all' },
    { label: 'Today', value: 'today' },
    { label: 'This week', value: 'week' },
    { label: 'This month', value: 'month' },
    { label: 'Last 30 days', value: 'last30' },
    { label: 'Custom range', value: 'custom' }
  ];
  selectedTimeframe = 'all';
  customStartDate = '';
  customEndDate = '';

  // Resolved once per filter change by syncActiveRange(), not per render.
  activeRange: DateRange = {};
  hasDateFilter = false;
  activeRangeLabel = 'All time';

  // Only meaningful once a Timeframe window is active: it re-scopes that same
  // window from "last contacted then" (the API's default) to "added then".
  dateBasisOptions: { label: string; value: 'latest_contact' | 'lead_added' }[] = [
    { label: 'Latest contact', value: 'latest_contact' },
    { label: 'Lead added', value: 'lead_added' }
  ];
  selectedDateBasis: 'latest_contact' | 'lead_added' = 'latest_contact';

  // Usage over the selected window, or all-time when none is selected. All
  // three figures come from one getReportMetrics call.
  creditsUsed: number | null = null;
  /** What the client is charged for: talk time rounded up to their pulse. */
  billedMinutes: number | null = null;
  /** Billed minutes include a rate-change day the backend couldn't split per call. */
  billedMinutesApprox = false;
  /** Talk time actually spent on connected calls. Always <= billed. */
  minutesCalled: number | null = null;
  usageLoading = false;

  humanReviewPanelCall: any = null;
  humanReviewPanelPosition: { [key: string]: string } = {};
  pageNumbers: Array<number | string> = [];

  private normalizePagination(pagination: any): any {
    if (!pagination) return null;
    const toNum = (v: any) => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : v;
    };

    // Handle both old format (currentPageNum, totalPages) and new format (current_page, total_pages)
    return {
      ...pagination,
      // New API format mapping
      currentPageNum: toNum(pagination.currentPageNum || pagination.current_page),
      totalPages: toNum(pagination.totalPages || pagination.total_pages),
      currentPageSize: toNum(pagination.currentPageSize || pagination.page_size),
      totalElements: toNum(pagination.totalElements || pagination.total_records),
      // Keep original fields for backward compatibility
      has_next_page: pagination.has_next_page,
      has_previous_page: pagination.has_previous_page,
    };
  }

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private notify: NotificationService,
    public enterpriseAdminService: EnterpriseAdminService,
    private twilioService: TwilioService,
    private jobService: JobService,
    private dialog: MatDialog,
    private workflowDataService: WorkflowDataService,
    private cd: ChangeDetectorRef,
    private outreachActivityService: OutreachActivityService,
    private reportsService: ReportsService,
  ) { }

  ngOnInit() {
    this.route.params.subscribe(data => {
      this.projectId = data.id;
      this.syncActivityStream();
    });

    // One-shot load — no continuous table polling; use Refresh for updates.
    if (this.projectDetails && this.projectDetails.project_id) {
      this.applyWorkflowFromProject(this.projectDetails);
      this.getDBCallsHistory(this.pageSize, this.pageIndex);
      this.syncActivityStream();
      // Only poll workflow status while a run is actively in progress
      if (this.process === 'In progress') {
        this.startWorkflowStatusPolling();
      }
    } else if (this.projectId) {
      this.getProjectById(this.profile.entity.id, this.projectId);
    }

    this.fetchLeadsStatusCount();
    this.fetchUsage();
  }
  getProjectById(entityId: string, projectId: string) {
    this.isLoading = true;
    this.jobService.getHoaProject(entityId, projectId).subscribe((res: any) => {
      this.isLoading = false;
      if (res) {
        this.projectDetails = res;
        this.applyWorkflowFromProject(res);
        this.initializeStatusFilterOptions();
        this.getDBCallsHistory(this.pageSize, this.pageIndex);
        this.syncActivityStream();
        if (this.process === 'In progress') {
          this.startWorkflowStatusPolling();
        } else {
          this.stopWorkflowStatusPolling();
        }
      }
    }, (err) => {
      this.isLoading = false;
      console.error('Failed to fetch project details:', err);
    });
  }

  /**
   * Manual refresh for connected leads table + status cards + project workflow snapshot.
   * Replaces continuous table polling.
   */
  refreshConnectedLeads(): void {
    if (this.isRefreshing || this.isLoading) {
      return;
    }
    this.isRefreshing = true;
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectId || (this.projectDetails && this.projectDetails.project_id);
    if (!entityId || !projectId) {
      this.isRefreshing = false;
      return;
    }

    this.jobService.getHoaProject(entityId, projectId).subscribe(
      (res: any) => {
        if (res) {
          this.projectDetails = res;
          this.applyWorkflowFromProject(res);
          this.initializeStatusFilterOptions();
          if (this.process === 'In progress') {
            this.startWorkflowStatusPolling();
          } else {
            this.stopWorkflowStatusPolling();
          }
        }
        this.getDBCallsHistory(this.pageSize, this.pageIndex, () => {
          this.isRefreshing = false;
        });
        this.fetchLeadsStatusCount();
        this.syncActivityStream();
        this.cd.markForCheck();
      },
      (err) => {
        console.error('Failed to refresh connected leads:', err);
        // Still try to refresh the table
        this.getDBCallsHistory(this.pageSize, this.pageIndex, () => {
          this.isRefreshing = false;
        });
        this.fetchLeadsStatusCount();
      }
    );
  }

  private applyWorkflowFromProject(res: any): void {
    if (!res || !res.workflow) {
      return;
    }
    this.selectedLeads = res.workflow.selectedLeads;
    this.processedLeads = res.workflow.processedLeads;
    this.requiredLeads = res.workflow.requiredLeads;
    this.process = res.workflow.process;
    if (res.workflow.process === 'Paused') {
      this.workflowPaused = true;
    } else {
      this.workflowPaused = false;
    }
    if (res.workflow.process === 'In progress') {
      this.showWorkflowStatus = true;
      this.showActivityStream = true;
    } else if (res.workflow.process) {
      this.showWorkflowStatus = true;
    }
  }

  private stopWorkflowStatusPolling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.workflowPollTicks = 0;
  }

  /**
   * Light polling of workflow status only while process === 'In progress'.
   * Does not re-fetch the full call table every tick unless workflow state changes.
   */
  private startWorkflowStatusPolling(): void {
    this.stopWorkflowStatusPolling();
    this.workflowPollTicks = 0;
    this.intervalId = setInterval(() => {
      this.workflowPollTicks++;
      if (this.workflowPollTicks > this.workflowPollMaxTicks) {
        this.stopWorkflowStatusPolling();
        return;
      }
      this.fetchWorkflowStatusOnce();
    }, this.workflowPollMs);
  }

  private fetchWorkflowStatusOnce(): void {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectId || (this.projectDetails && this.projectDetails.project_id);
    if (!entityId || !projectId) {
      return;
    }
    this.jobService.getHoaProject(entityId, projectId).subscribe(
      (res: any) => {
        if (!res) {
          return;
        }
        const processChanged = this.process !== (res.workflow && res.workflow.process);
        const workflowChanged =
          JSON.stringify(this.projectDetails && this.projectDetails.workflow) !==
          JSON.stringify(res.workflow);

        this.projectDetails = res;
        this.applyWorkflowFromProject(res);
        this.initializeStatusFilterOptions();

        if (this.process === 'In progress') {
          if (workflowChanged || processChanged) {
            this.getDBCallsHistory(this.pageSize, this.pageIndex);
            this.fetchLeadsStatusCount();
          }
          this.cd.markForCheck();
        } else {
          // Workflow finished / paused / idle — stop auto polling
          this.stopWorkflowStatusPolling();
          if (processChanged) {
            this.getDBCallsHistory(this.pageSize, this.pageIndex);
            this.fetchLeadsStatusCount();
          }
          this.cd.markForCheck();
        }
      },
      () => undefined
    );
  }
  ngOnDestroy() {
    this.stopWorkflowStatusPolling();
    this.stopJevOutcomeBackfillPolling();
    this.stopDeleteTasksPolling();
    if (this.callHistoryIntervalId) {
      clearInterval(this.callHistoryIntervalId);
      this.callHistoryIntervalId = null;
    }
    this.teardownActivityStream();
    this.resetAudio();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['callData'] && changes['callData'].currentValue) {
      this.isLoading = true;
      // Populate with skeleton data while loading
      this.dataSource = Array(this.pageSize).fill({});

      const callData = changes['callData'].currentValue;
      this.isLoading = false;
      if (this.dataSource && this.dataSource.length) {
        this.dataSource.splice(0, this.dataSource.length, ...callData);
      } else {
        this.dataSource = callData;
      }
      this.pagination = this.normalizePagination(callData.pagination);
      this.pageNumbers = this.pagination ? this.buildPageNumbers(this.pagination) : [];
      this.updateRange();
      this.cd.markForCheck();
    } else if (changes['projectDetails'] && changes['projectDetails'].currentValue && (!this.dataSource || this.dataSource.length === 0)) {
      // Initialize skeleton data when projectDetails changes
      this.isLoading = true;
      this.dataSource = Array(this.pageSize).fill({});
    }
    if (changes['projectDetails'] && changes['projectDetails'].currentValue) {
      if (this.projectDetails && this.projectDetails.expected_outcomes) {
        // Show all boxes instantly
        this.expectedOutcomes = this.projectDetails.expected_outcomes.map(outcome => ({
          ...outcome,
          count: 0  // Initially set 0
        }));

        // Initialize status filter options
        this.initializeStatusFilterOptions();
      }
      this.syncActivityStream();
    }
  }

  calculateExpectedOutcomes() {
    // No longer needed - using API data from leadsStatusCount
  }

  // Helper method to initialize status filter options from project details
  initializeStatusFilterOptions() {
    if (this.projectDetails && this.projectDetails.expected_outcomes) {
      this.statusFilterOptions = this.projectDetails.expected_outcomes.map(outcome => outcome.outcome_name);
    }
  }

  // Select all rows 
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.length;
    return numSelected === numRows;
  }

  masterToggle(event: any) {
    if (event.checked) {
      this.isAllSelected() ?
        this.selection.clear() :
        this.dataSource.forEach(row => { this.selection.select(row) });
      this.isAllSelected() ? this.allRowsSelected = true : this.allRowsSelected = false;
    } else {
      this.selection.clear();
    }
  }

  callSelectedAllUsers() {
    //console.log(this.selection, this.projectDetails)
    let callObj = [];
    if (this.selection && this.selection.selected.length) {
      this.selection.selected.forEach(element => {
        let obj = {
          "client_name": element.client_name,
          "client_email": element.client_email,
          "client_number": element.client_number,
          "entity_id": this.profile.entity.id,
          "tts": "elevenlabs",
          "language": this.projectDetails.language || "en",
          "voice": this.projectDetails.voice || "male",
        }
        callObj.push(obj);
      });

      this.enterpriseAdminService.callSelectedUsers(this.projectDetails.agent_id, this.projectDetails.project_id, this.projectDetails.entity_id, callObj).subscribe(res => {
        this.ensureActivityStream();
        this.notify.showMessage(
          `Connecting users...`,
          30000
        );
        // No status polling — use live activity stream + manual Refresh
      }, (err => {
        console.log(err)
      }))
    } else {
      this.notify.showMessage(
        `Please select users first!`,
        30000
      );
    }
  }

  // --- Re-evaluate selected calls ----------------------------------------
  // Re-runs the post-call analysis against each row's stored transcript using
  // the project's CURRENT outcome definitions, so a QA can prove a definition
  // change without dialling a real call. This commits, and runs the same
  // follow-up actions a finished call would - hence the confirm step.
  private readonly reevaluateChunkSize = 25;
  reevaluateArmed = false;
  isReevaluating = false;
  reevaluateProgress = { done: 0, total: 0 };
  reevaluateSummary: any = null;

  get selectedCount(): number {
    return this.selection && this.selection.selected ? this.selection.selected.length : 0;
  }

  /** Ask before running: re-evaluating dispatches real follow-up actions. */
  armReevaluate() {
    if (!this.selectedCount) {
      this.notify.showMessage('Please select calls first!', 5000);
      return;
    }
    this.reevaluateSummary = null;
    this.reevaluateArmed = true;
  }

  cancelReevaluate() {
    this.reevaluateArmed = false;
  }

  confirmReevaluate() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;
    const jobIds = this.selection.selected
      .map((row: any) => row && row.job_id)
      .filter((id: any) => !!id);

    if (!entityId || !projectId) {
      this.notify.showMessage('Missing project or session information', 5000);
      return;
    }
    if (!jobIds.length) {
      this.notify.showMessage('None of the selected rows has a call to re-evaluate', 5000);
      return;
    }

    // The endpoint caps a request at 25 so it fits inside API Gateway's 29s
    // window; larger selections go up in order, one chunk at a time.
    const chunks: string[][] = [];
    for (let i = 0; i < jobIds.length; i += this.reevaluateChunkSize) {
      chunks.push(jobIds.slice(i, i + this.reevaluateChunkSize));
    }

    this.reevaluateArmed = false;
    this.isReevaluating = true;
    this.reevaluateProgress = { done: 0, total: jobIds.length };
    const results: any[] = [];

    const runChunk = (index: number) => {
      if (index >= chunks.length) {
        this.finishReevaluate(results);
        return;
      }
      this.jobService.reevaluateCallAnalysis(entityId, {
        project_id: projectId,
        job_ids: chunks[index]
      }).subscribe(
        (response: any) => {
          const rows = (response && response.results) || [];
          rows.forEach((row: any) => results.push(row));
          this.reevaluateProgress = {
            done: Math.min(this.reevaluateProgress.done + chunks[index].length, this.reevaluateProgress.total),
            total: this.reevaluateProgress.total
          };
          this.cd.detectChanges();
          runChunk(index + 1);
        },
        (err: any) => {
          // Keep whatever earlier chunks already committed - they are saved.
          const msg =
            (err && err.error && err.error.error) ||
            (err && err.error && err.error.message) ||
            'Failed to re-evaluate these calls';
          this.notify.showMessage(msg, 6000);
          this.finishReevaluate(results);
        }
      );
    };

    runChunk(0);
  }

  private finishReevaluate(results: any[]) {
    this.isReevaluating = false;
    const changed = results.filter(r => r && r.changed).length;
    const reevaluated = results.filter(r => r && !r.skipped).length;
    const skipped = results.filter(r => r && r.skipped);

    // Group the skips so the strip says "3 WhatsApp, 1 already reviewed"
    // rather than listing every row.
    const skipCounts: { [reason: string]: { count: number; message: string } } = {};
    skipped.forEach(row => {
      const reason = row.skip_reason || 'skipped';
      if (!skipCounts[reason]) {
        skipCounts[reason] = { count: 0, message: row.message || reason };
      }
      skipCounts[reason].count++;
    });

    this.reevaluateSummary = {
      reevaluated,
      changed,
      skipped: skipped.length,
      skipReasons: Object.keys(skipCounts).map(reason => ({
        reason,
        count: skipCounts[reason].count,
        message: skipCounts[reason].message
      })),
      changedRows: results.filter(r => r && r.changed).map(r => ({
        client_name: r.client_name,
        previous_status: r.previous_status,
        new_status: r.new_status,
        goal_outcome_held: r.goal_outcome_held
      }))
    };

    if (reevaluated) {
      this.notify.showMessage(
        `Re-evaluated ${reevaluated} call${reevaluated === 1 ? '' : 's'} — ${changed} outcome${changed === 1 ? '' : 's'} changed`,
        5000
      );
      this.selection.clear();
      this.getDBCallsHistory(this.pageSize, this.pageIndex);
      this.fetchLeadsStatusCount();
    } else if (skipped.length) {
      this.notify.showMessage('None of the selected calls could be re-evaluated', 5000);
    }
    this.cd.detectChanges();
  }

  dismissReevaluateSummary() {
    this.reevaluateSummary = null;
  }

  // --- Backfill Jev outcome (whole project) -------------------------------
  // Independent second-opinion classifier (OpenRouter Jev) run against every
  // call in this project, for comparison against the Outcome column only -
  // writes just jev_outcome / jev_outcome_confidence, never status, never
  // call actions. No confirm step needed (unlike Re-evaluate), and it runs
  // against the whole project rather than a selection since a project can
  // have thousands of leads - too many to hand-pick.
  // Hidden from Connected Leads for now (buttons, column, and mobile-card
  // line) - flip this back on when the feature is ready to ship; nothing
  // else here needs to change.
  showJevOutcome = false;
  private jevBackfillPollIntervalId: any;
  private readonly jevBackfillPollMs = 3000;
  private readonly jevBackfillPollMaxTicks = 400; // ~20 min max, then stop
  private jevBackfillPollTicks = 0;
  isBackfillingJevOutcome = false;
  jevBackfillProgress = { done: 0, total: 0 };
  jevBackfillSummary: any = null;

  /** True when Jev's pick matches the call's current outcome. */
  jevOutcomeAgrees(call: any): boolean {
    return !!(call && call.jev_outcome && call.jev_outcome === call.status);
  }

  /**
   * Jev never explains a pick - it can't generate text - so the closest
   * thing to "why" is how confident or close the decision was: its full
   * probability distribution across every candidate outcome, sorted
   * highest first, e.g. "Fully Qualified: 55% · Partially Qualified: 40% ·
   * Not Interested: 5%".
   */
  jevOutcomeProbabilitiesTooltip(call: any): string {
    const probabilities = call && call.jev_outcome_probabilities;
    if (!probabilities || typeof probabilities !== 'object') {
      return 'Jev\'s independent outcome pick, for comparison - never used to set the real outcome';
    }
    const rows = Object.keys(probabilities)
      .map(outcome => ({ outcome, value: Number(probabilities[outcome]) || 0 }))
      .sort((a, b) => b.value - a.value)
      .map(row => `${row.outcome}: ${Math.round(row.value * 100)}%`);
    return rows.length ? rows.join(' · ') : 'Jev\'s independent outcome pick, for comparison - never used to set the real outcome';
  }

  startJevOutcomeBackfill() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;
    if (!entityId || !projectId) {
      this.notify.showMessage('Missing project or session information', 5000);
      return;
    }

    this.jevBackfillSummary = null;
    this.isBackfillingJevOutcome = true;
    this.jevBackfillProgress = { done: 0, total: 0 };

    this.jobService.startBackfillJevOutcomeProject(entityId, { project_id: projectId }).subscribe(
      (res: any) => {
        if (!res || !res.job_id) {
          this.isBackfillingJevOutcome = false;
          this.notify.showMessage('Could not start the Jev backfill', 5000);
          return;
        }
        this.jevBackfillProgress = { done: 0, total: res.total_leads || 0 };
        this.cd.detectChanges();
        this.startJevOutcomeBackfillPolling(projectId, res.job_id);
      },
      (err: any) => {
        this.isBackfillingJevOutcome = false;
        const msg =
          (err && err.error && err.error.error) || 'Could not start the Jev backfill';
        this.notify.showMessage(msg, 6000);
      }
    );
  }

  private stopJevOutcomeBackfillPolling(): void {
    if (this.jevBackfillPollIntervalId) {
      clearInterval(this.jevBackfillPollIntervalId);
      this.jevBackfillPollIntervalId = null;
    }
    this.jevBackfillPollTicks = 0;
  }

  private startJevOutcomeBackfillPolling(projectId: string, jobId: string): void {
    this.stopJevOutcomeBackfillPolling();
    this.jevBackfillPollIntervalId = setInterval(() => {
      this.jevBackfillPollTicks++;
      if (this.jevBackfillPollTicks > this.jevBackfillPollMaxTicks) {
        this.stopJevOutcomeBackfillPolling();
        this.isBackfillingJevOutcome = false;
        this.notify.showMessage(
          'Jev backfill is taking longer than expected - it is still running in the background',
          6000
        );
        return;
      }
      this.jobService.getBackfillJevOutcomeProjectStatus(projectId, jobId).subscribe(
        (status: any) => this.handleJevOutcomeBackfillStatus(status),
        () => {
          // Transient poll failure - keep trying until max ticks.
        }
      );
    }, this.jevBackfillPollMs);
  }

  private handleJevOutcomeBackfillStatus(status: any) {
    if (!status) {
      return;
    }
    this.jevBackfillProgress = {
      done: status.processed_leads || 0,
      total: status.total_leads || this.jevBackfillProgress.total
    };
    this.cd.detectChanges();

    if (status.status === 'completed') {
      this.stopJevOutcomeBackfillPolling();
      this.finishJevOutcomeBackfill(status);
    }
  }

  private finishJevOutcomeBackfill(status: any) {
    this.isBackfillingJevOutcome = false;
    const processed = (status && status.processed_leads) || 0;
    const agrees = (status && status.agrees_with_status) || 0;
    const errorCount = (status && status.errors && status.errors.length) || 0;

    this.jevBackfillSummary = { processed, agrees, errorCount };

    if (processed) {
      this.notify.showMessage(
        `Jev analysed ${processed} call${processed === 1 ? '' : 's'} — agrees with the current outcome on ${agrees}`,
        5000
      );
      this.getDBCallsHistory(this.pageSize, this.pageIndex);
    } else {
      this.notify.showMessage('No calls in this project had a transcript for Jev to analyse', 5000);
    }
    this.cd.detectChanges();
  }

  dismissJevBackfillSummary() {
    this.jevBackfillSummary = null;
  }

  // --- Delete follow-up tasks (whole project, or by outcome) ---------------
  // Disables (cancels) pending schedules/open tasks via editProjectTasks -
  // the same async job pauseWorkflow already fires. An empty status selection
  // means "every lead in the project", matching resolve_client_numbers' own
  // fallback on the backend - confirmed explicitly because that empty-selection
  // case is the most destructive one, which is why it's confirmed via a dialog
  // instead of a plain window.confirm (which also freezes automated testing).
  isDeletingTasks = false;
  deleteTasksSummary: any = null;
  private deleteTasksPollIntervalId: any;
  private readonly deleteTasksPollMs = 3000;
  private readonly deleteTasksPollMaxTicks = 400; // ~20 min max, then stop
  private deleteTasksPollTicks = 0;

  openDeleteTasksDialog(): void {
    this.deleteTasksSummary = null;
    const dialogRef = this.dialog.open(DeleteFollowUpTasksDialogComponent, {
      panelClass: 'dft-dialog-panel',
      disableClose: true,
      data: { statusOptions: this.statusFilterOptions }
    });

    dialogRef.afterClosed().subscribe((statuses: string[] | undefined) => {
      if (statuses === undefined) {
        return;
      }
      this.runDeleteTasks(statuses);
    });
  }

  private runDeleteTasks(statuses: string[]): void {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;
    if (!entityId || !projectId) {
      this.notify.showMessage('Missing project or session information', 5000);
      return;
    }

    this.isDeletingTasks = true;

    this.jobService.editProjectTasks(
      entityId, projectId, 'disable',
      statuses.length ? statuses : undefined,
      'Bulk delete via Connected Leads'
    ).subscribe(
      (res: any) => {
        if (!res || !res.job_id) {
          this.isDeletingTasks = false;
          this.notify.showMessage('Could not start deleting these tasks', 5000);
          return;
        }
        this.startDeleteTasksPolling(projectId, res.job_id);
      },
      (err: any) => {
        this.isDeletingTasks = false;
        const msg = (err && err.error && err.error.error) || 'Could not start deleting these tasks';
        this.notify.showMessage(msg, 6000);
      }
    );
  }

  private stopDeleteTasksPolling(): void {
    if (this.deleteTasksPollIntervalId) {
      clearInterval(this.deleteTasksPollIntervalId);
      this.deleteTasksPollIntervalId = null;
    }
    this.deleteTasksPollTicks = 0;
  }

  private startDeleteTasksPolling(projectId: string, jobId: string): void {
    this.stopDeleteTasksPolling();
    this.deleteTasksPollIntervalId = setInterval(() => {
      this.deleteTasksPollTicks++;
      if (this.deleteTasksPollTicks > this.deleteTasksPollMaxTicks) {
        this.stopDeleteTasksPolling();
        this.isDeletingTasks = false;
        this.notify.showMessage(
          'This is taking longer than expected - it is still running in the background',
          6000
        );
        return;
      }
      this.jobService.getEditProjectTasksStatus(projectId, jobId).subscribe(
        (status: any) => {
          if (status && status.status === 'completed') {
            this.stopDeleteTasksPolling();
            this.finishDeleteTasks(status);
          }
          this.cd.detectChanges();
        },
        () => {
          // Transient poll failure - keep trying until max ticks.
        }
      );
    }, this.deleteTasksPollMs);
  }

  private finishDeleteTasks(status: any): void {
    this.isDeletingTasks = false;
    const matchedLeads = (status && status.matched_leads) || 0;
    const cancelledJobs = (status && status.cancelled_jobs) || 0;
    const cancelledTasks = (status && status.cancelled_task_count) || 0;
    const errorCount = (status && status.errors && status.errors.length) || 0;

    this.deleteTasksSummary = { matchedLeads, cancelledJobs, cancelledTasks, errorCount };

    this.notify.showMessage(
      matchedLeads
        ? `Deleted follow-up tasks for ${matchedLeads} lead${matchedLeads === 1 ? '' : 's'} — `
          + `${cancelledJobs} schedule${cancelledJobs === 1 ? '' : 's'} cancelled`
        : 'No leads matched - nothing to delete',
      5000
    );
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
    this.cd.detectChanges();
  }

  dismissDeleteTasksSummary(): void {
    this.deleteTasksSummary = null;
  }

  // --- Backfill Jev outcome (selected calls) -------------------------------
  // Same jev_outcome / jev_outcome_confidence write as the whole-project
  // button above, but scoped to the checked rows - for when you want Jev's
  // opinion on specific calls rather than the entire project. No confirm
  // step needed here either, same reason: no status change, no call
  // actions. Chunks client-side like Re-evaluate does, since the endpoint
  // caps a request at 25 job_ids.
  private readonly jevBackfillSelectedChunkSize = 25;

  backfillJevOutcomeSelected() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;
    const jobIds = this.selection.selected
      .map((row: any) => row && row.job_id)
      .filter((id: any) => !!id);

    if (!entityId || !projectId) {
      this.notify.showMessage('Missing project or session information', 5000);
      return;
    }
    if (!jobIds.length) {
      this.notify.showMessage('None of the selected rows has a call to analyse', 5000);
      return;
    }

    const chunks: string[][] = [];
    for (let i = 0; i < jobIds.length; i += this.jevBackfillSelectedChunkSize) {
      chunks.push(jobIds.slice(i, i + this.jevBackfillSelectedChunkSize));
    }

    this.jevBackfillSummary = null;
    this.isBackfillingJevOutcome = true;
    this.jevBackfillProgress = { done: 0, total: jobIds.length };
    const results: any[] = [];

    const runChunk = (index: number) => {
      if (index >= chunks.length) {
        this.finishJevOutcomeBackfillSelected(results);
        return;
      }
      this.jobService.backfillJevOutcome(entityId, {
        project_id: projectId,
        job_ids: chunks[index]
      }).subscribe(
        (response: any) => {
          const rows = (response && response.results) || [];
          rows.forEach((row: any) => results.push(row));
          this.jevBackfillProgress = {
            done: Math.min(this.jevBackfillProgress.done + chunks[index].length, this.jevBackfillProgress.total),
            total: this.jevBackfillProgress.total
          };
          this.cd.detectChanges();
          runChunk(index + 1);
        },
        (err: any) => {
          const msg =
            (err && err.error && err.error.error) || 'Failed to analyse these calls with Jev';
          this.notify.showMessage(msg, 6000);
          this.finishJevOutcomeBackfillSelected(results);
        }
      );
    };

    runChunk(0);
  }

  private finishJevOutcomeBackfillSelected(results: any[]) {
    this.isBackfillingJevOutcome = false;
    const processed = results.filter(r => r && !r.skipped).length;
    const agrees = results.filter(r => r && r.agrees_with_status).length;
    const errorCount = results.filter(r => r && r.skip_reason === 'backfill_failed').length;

    this.jevBackfillSummary = { processed, agrees, errorCount };

    if (processed) {
      this.notify.showMessage(
        `Jev analysed ${processed} call${processed === 1 ? '' : 's'} — agrees with the current outcome on ${agrees}`,
        5000
      );
      this.getDBCallsHistory(this.pageSize, this.pageIndex);
    } else {
      this.notify.showMessage('None of the selected calls could be analysed by Jev', 5000);
    }
    this.cd.detectChanges();
  }

  getUpdatingStatus(job_id) {
    this.enterpriseAdminService.getUpdatedStatus(job_id, this.profile.entity.id,).subscribe(res => {

    }, (err => {
      console.log(err)
    }))
  }

  connectCall(call: any) {
    let obj = [{
      "client_name": call.client_name,
      "client_email": call.client_email,
      "client_number": call.client_number,
      "entity_id": this.profile.entity.id,
      "tts": "elevenlabs",
      "language": this.projectDetails.language || "en",
      "voice": this.projectDetails.voice || "male",
      "people_id": call.people_id,
    }]

    this.enterpriseAdminService.callSelectedUsers(this.projectDetails.agent_id, this.projectDetails.project_id, this.projectDetails.entity_id, obj).subscribe(res => {
      this.ensureActivityStream();
      this.notify.showMessage(
        `Calling user...`,
        30000
      );
      // No status polling — use live activity stream + manual Refresh
    }, (err => {
      console.log(err)
    }))
  }

  exportData() {

  }

  // Pagination helper methods
  getPageNum(): number {
    if (this.pagination && this.pagination.currentPageNum != null) {
      return Number(this.pagination.currentPageNum) || 1;
    }
    return this.pageIndex || 1;
  }

  getTotalPages(): number {
    if (this.pagination && this.pagination.totalPages != null) {
      return Number(this.pagination.totalPages) || 0;
    }
    return 0;
  }

  canPrev(): boolean {
    if (!this.pagination) return false;
    // Check has_previous_page if available (new API format)
    if (this.pagination.has_previous_page !== undefined) {
      return this.pagination.has_previous_page === true;
    }
    // Fallback to page number check
    return this.getPageNum() > 1;
  }

  canNext(): boolean {
    if (!this.pagination) return false;
    // Check has_next_page if available (new API format)
    if (this.pagination.has_next_page !== undefined) {
      return this.pagination.has_next_page === true;
    }
    // Fallback to totalPages check
    const currentPage = this.getPageNum();
    const totalPages = this.getTotalPages();
    return totalPages > 0 && currentPage < totalPages;
  }

  onNext() {
    if (!this.canNext() || this.isLoading) return;
    const nextPage = this.getPageNum() + 1;
    this.pageIndex = nextPage;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  onPrev() {
    if (!this.canPrev() || this.isLoading) return;
    const prevPage = this.getPageNum() - 1;
    this.pageIndex = prevPage;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  onPageSelect(page: number | string) {
    if (!this.pagination || typeof page !== 'number' || page === this.getPageNum() || this.isLoading) {
      return;
    }
    this.pageIndex = page;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  buildPageNumbers(pagination: any): Array<number | string> {
    if (!pagination) {
      return [];
    }

    const totalPages = this.getTotalPages();
    const currentPage = this.getPageNum();

    if (!totalPages || totalPages === 0) {
      return [];
    }

    const pages: Array<number | string> = [];

    const addPage = (page: number | string) => {
      if (pages[pages.length - 1] !== page) {
        pages.push(page);
      }
    };

    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    addPage(1);

    if (currentPage > 3) {
      addPage('...');
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      addPage(i);
    }

    if (currentPage < totalPages - 2) {
      addPage('...');
    }

    addPage(totalPages);

    return pages;
  }

  trackByPageNumber(index: number, _page: number | string): number {
    return index;
  }

  isEllipsis(page: number | string): boolean {
    return page === '...';
  }

  updateRange() {
    if (this.pagination && this.pagination.currentPageNum && this.pagination.currentPageSize && this.pagination.totalElements) {
      const start = (this.pagination.currentPageNum - 1) * this.pagination.currentPageSize + 1;
      const end = Math.min(this.pagination.currentPageNum * this.pagination.currentPageSize, this.pagination.totalElements);
      this.currentRange = `${start} - ${end} of ${this.pagination.totalElements} records`;
    } else {
      this.currentRange = '0 - 0 of 0 records'; // Default value in case pagination is not valid
    }
  }

  onPageSizeChange(value: any) {
    const newSize = parseInt(value, 10);
    this.pageSize = newSize;
    this.pageIndex = 1; // Reset to the first page after size change
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  triggerParentPagination() {
    //console.log("Pagination triggered for page:", this.pageIndex);
  }

  // Fetch and set recording URL for the audio player
  openRecording(call: any) {
    const callSid = call.call_sid;

    // Stop current audio if a new row is clicked
    if (this.playingRecordingId && this.playingRecordingId !== callSid) {
      this.stopRecording();
    }

    // Toggle playback if the same row is clicked again
    if (this.playingRecordingId === callSid) {
      this.stopRecording();
      return;
    }

    const recordingUrl = call.recording_url;

    if (!recordingUrl) {
      console.error('No recording URL available for this call.');
      return;
    }

    this.playRecording(callSid, recordingUrl);
  }
  stopRecording() {
    if (this.currentAudio) {
      this.currentAudio.pause();
    }
  }

  resetAudio() {
    if (this.currentAudio) {
      this.currentAudio.destroy();
      this.currentAudio = null;
      this.playingRecordingId = null;
      this.currentRecordingUrl = null;
    }
  }

  playRecording(callSid: string, audioUrl: string) {
    if (this.currentAudio && this.currentRecordingUrl === audioUrl) {
      if (this.currentAudio.paused) {
        this.currentAudio.play();
      } else {
        this.currentAudio.pause();
      }
      this.playingRecordingId = callSid;
      this.cd.markForCheck();
      return;
    }

    if (this.currentAudio && this.currentRecordingUrl !== audioUrl) {
      this.resetAudio();
    }

    this.currentAudio = new CspSafeAudioPlayer({
      onTimeUpdate: () => this.cd.markForCheck(),
      onDuration: () => this.cd.markForCheck(),
      onPlayingChange: () => this.cd.markForCheck(),
      onEnded: () => this.cd.markForCheck(),
      onError: (message) => {
        console.error('Recording playback failed:', audioUrl, message);
        this.cd.markForCheck();
      }
    });
    this.playingRecordingId = callSid;
    this.currentRecordingUrl = audioUrl;

    this.currentAudio.playUrl(audioUrl).catch((err) => {
      console.error('Recording playback failed:', audioUrl, getFetchAudioErrorMessage(err));
      this.cd.markForCheck();
    });
  }

  seekAudio(event: any) {
    if (this.currentAudio) {
      this.currentAudio.currentTime = parseFloat(event.target.value);
      this.cd.markForCheck();
    }
  }

  formatAudioTime(time: number | undefined): string {
    if (!time) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${this.padNumber(minutes)}:${this.padNumber(seconds)}`;
  }

  openEmploymentModal(employmentHistory: any[]) {
    this.selectedEmploymentHistory = employmentHistory || [];
    this.showEmploymentModal = true;
  }

  closeEmploymentModal() {
    this.showEmploymentModal = false;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }); // e.g., "Jun 2022"
  }

  revealContact(type: 'phone' | 'email', call: any) {
    //console.log(`Revealing ${type} for`, call);
    const people_id = call.people_id;
    const project_id = call.project_id;

    if (!call.public_profiles.linkedin) {
      console.error('No LinkedIn URL available for this candidate.');
      return;
    }

    this.enterpriseAdminService.fetchCandidatePersonalDetails(call.public_profiles.linkedin, type, people_id, project_id).subscribe(
      (response: any) => {
        if (type === 'phone') {
          call.client_number = response.phone_number || 'Fetching number...';
        } else {
          call.client_email = response.email || 'Fetching email...';
        }
      },
      (error) => {
        console.error('Error fetching candidate details:', error);
      }
    );
  }

  openWorkflowPopup() {
    // console.log(this.selection, this.projectDetails);
    this.workflowPopupData = [];
    if (this.selection && this.selection.selected.length) {
      this.selection.selected.forEach(element => {
        let obj = {
          "client_name": element.client_name,
          "client_email": element.client_email,
          "client_number": element.client_number,
          "entity_id": this.profile.entity.id,
          "project_id": this.projectDetails.project_id,
          "workflowType": "call"
        }
        this.workflowPopupData.push(obj);
      });

    } else {
      this.notify.showMessage(
        `Please select users first!`,
        30000
      );
    }

    const dialogRef = this.dialog.open(WorkflowPopupComponent, {
      width: '640px',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: this.workflowPopupData
    });

    // Listen for the event from popup
    dialogRef.componentInstance.workflowSetup.subscribe((result) => {
      // console.log("Workflow setup data:", result);

      // ✅ Store data in service before navigation
      this.workflowDataService.setWorkflowData(this.workflowPopupData);

      // Navigate to workflow-stepper page with project ID and workflow type query param
      const workflowType = (result && result.workflowType) ? result.workflowType : null;
      const navigationExtras = workflowType ? { queryParams: { workflowType: workflowType } } : {};
      this.router.navigate([`/job/view-new-project/${this.projectDetails.project_id}/setup-workflow`], navigationExtras);
    });

    dialogRef.afterClosed().subscribe(result => {
      // console.log("Popup closed");
    });
  }

  getDBCallsHistory(maxPageSize: number, pageNum: number, onDone?: () => void) {
    this.isLoading = true;
    // Populate with skeleton data immediately
    this.dataSource = Array(maxPageSize).fill({});

    const filters = this.buildCallHistoryFilters();
    const projectId = this.projectId || (this.projectDetails && this.projectDetails.project_id);

    this.enterpriseAdminService.getCallHistoryProject(this.profile.entity.id, projectId, maxPageSize, pageNum, undefined, filters).subscribe(res => {
      this.isLoading = false;
      if (onDone) {
        onDone();
      }

      if (res && res.data && Array.isArray(res.data)) {
        // Format timestamps for each record (handle both string and number)
        this.dataSource = res.data.map(ele => {
          if (ele && ele.start_time) {
            // Handle both string and number timestamps
            const timestamp = typeof ele.start_time === 'string' ? parseFloat(ele.start_time) : ele.start_time;
            if (typeof timestamp === 'number' && !isNaN(timestamp) && timestamp > 0) {
              ele.start_time = this.formatStartTime(timestamp);
            }
          }
          return ele;
        });

        this.pagination = this.normalizePagination(res.pagination) || null;
        this.metadata = res.metadata || null;

        // Build page numbers for pagination
        if (this.pagination) {
          this.pageNumbers = this.buildPageNumbers(this.pagination);
          this.updateRange();
        } else {
          this.pageNumbers = [];
        }

        // Clear selection when data changes
        this.selection.clear();
      } else {
        // If no response or no data, set empty array
        this.dataSource = [];
        this.pagination = null;
        this.metadata = null;
        this.pageNumbers = [];
      }

      this.cd.detectChanges();
    }, (err => {
      this.isLoading = false;
      if (onDone) {
        onDone();
      }
      console.error('Error fetching call history:', err);
      // On error, set empty data
      this.dataSource = [];
      this.pagination = null;
      this.metadata = null;
      this.pageNumbers = [];
      this.cd.detectChanges();
    }))
  }

  formatStartTime(value: any): string {
    const timestamp = parseFloat(value) * 1000; // Convert to milliseconds
    const date = new Date(timestamp);

    // Get the day, month, and year
    const day = date.getDate();
    const month = this.getMonthAbbreviation(date.getMonth()); // Get month abbreviation (e.g., "Dec")
    const year = date.getFullYear();

    // Get the time components (hours, minutes, seconds)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Format time in 12-hour format with AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = this.padNumber(minutes);
    const formattedSeconds = this.padNumber(seconds);

    // Return formatted string like 'Dec 31, 2024, 5:04:39 PM'
    return `${month} ${day}, ${year}, ${formattedHours}:${formattedMinutes}:${formattedSeconds} ${period}`;
  }

  // Helper method to get month abbreviation (e.g., 'Jan' for January)
  getMonthAbbreviation(month: number): string {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return months[month] || '';
  }

  // Helper method to pad single digits for minutes and seconds
  padNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  getFirstOpenTask(call: any): any | null {
    if (!call || !call.open_tasks || !call.open_tasks.tasks || !call.open_tasks.tasks.length) {
      return null;
    }
    return call.open_tasks.tasks[0];
  }

  getTaskDescription(task: any): string {
    if (!task) {
      return '';
    }
    return task.task_description || task.task_prompt || '';
  }

  formatTaskDatetime(task: any): string | null {
    if (!task || task.task_datetime == null || task.task_datetime === '') {
      return null;
    }

    const raw = task.task_datetime;
    let date: Date;

    if (typeof raw === 'number') {
      date = new Date(raw < 1e12 ? raw * 1000 : raw);
    } else if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
      const num = Number(raw);
      date = new Date(num < 1e12 ? num * 1000 : num);
    } else {
      date = new Date(raw);
    }

    if (isNaN(date.getTime())) {
      return null;
    }

    const day = date.getDate();
    const month = this.getMonthAbbreviation(date.getMonth());
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = this.padNumber(minutes);

    return `${month} ${day}, ${year} · ${formattedHours}:${formattedMinutes} ${period}`;
  }

  seeCallSummary(call: any) {
    const dialogRef = this.dialog.open(CallSummaryPopupComponent, {
      width: '800px',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        status: call.status,
        status_reason: call.status_reason,
        call_transcript: call.call_transcript,
        client_name: call.client_name,
        questions_asked: call.questions_asked,
        call_sid: call.call_sid,
        recording_url: call.recording_url,
        job_id: call.job_id,
        project_id: call.project_id || this.projectId,
        // The popup can now correct the outcome / reason / answers, so it needs
        // the keys the endpoint resolves the row from and the allowed outcomes.
        people_id: call.people_id,
        ai_outcome: call.ai_outcome,
        human_reviewed_by: call.human_reviewed_by,
        human_reviewed_at: call.human_reviewed_at,
        // `false` means the outcome's push into another project is awaiting a
        // human; absent means this outcome never asked for one.
        add_in_other_project_approved: call.add_in_other_project_approved,
        expectedOutcomes: (this.projectDetails && this.projectDetails.expected_outcomes) || [],
        // Post-call signals (score / strength / reason / task_prompt).
        signals: this.getCallSignals(call),
        entityId: this.profile && this.profile.entity && this.profile.entity.id,
        photo_url: call.photo_url || '',
        client_number: call.client_number || '',
        task_prompt: resolveLeadTaskPrompt(call),
      }
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.getDBCallsHistory(this.pageSize, this.pageIndex);
        this.fetchLeadsStatusCount();
      }
    });
  }

  handleOpenTasksClick(call: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    event.stopImmediatePropagation();
    this.openEditTasksDialog(call, event);
  }

  handleSignalClick(call: any, signal: PostCallSignal, event: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    this.openSignalDetail(call, signal);
  }

  openEditOutcomeDialog(call: any, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    const dialogRef = this.dialog.open(EditOutcomePopupComponent, {
      width: '400px',
      maxWidth: '95vw',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      autoFocus: true,
      data: {
        call,
        projectId: this.projectDetails && this.projectDetails.project_id,
        expectedOutcomes: (this.projectDetails && this.projectDetails.expected_outcomes) || []
      }
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.getDBCallsHistory(this.pageSize, this.pageIndex);
        this.fetchLeadsStatusCount();
      }
    });
  }

  openEditTasksDialog(call: any, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    const dialogRef = this.dialog.open(EditTasksPopupComponent, {
      width: '600px',
      height: '80vh',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        open_tasks: call.open_tasks,
        closed_tasks: call.closed_tasks,
        client_name: call.client_name,
        call_id: call.call_sid,
        people_id: call.people_id,
        job_id: call.job_id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getDBCallsHistory(this.pageSize, this.pageIndex);
      }
    });
  }

  viewClientProfile(call: any, event?: Event) {
    // Don't open profile if clicking on open tasks section
    if (event && event.target) {
      const target = event.target as HTMLElement;
      if (
        target.closest('.open-tasks-section')
        || target.closest('.lead-documents-section')
        || target.closest('.signals-cell')
        || target.closest('.signal-row--clickable')
      ) {
        return;
      }
    }
    const projectId = (this.projectDetails && this.projectDetails.project_id) ? this.projectDetails.project_id : null;
    const dialogRef = this.dialog.open(ClientProfileComponent, {
      disableClose: true,
      width: '45%',
      height: '100vh',
      position: { right: '0', top: '0', bottom: '0' },
      panelClass: ['add-edit-role-dialog', 'client-profile-panel'],
      data: {
        projectDetails: this.projectDetails,
        projectId: projectId,
        callDetails: {
          people_id: call.people_id,
          project_id: call.project_id || projectId,
          client_number: call.client_number,
          client_name: call.client_name,
          client_email: call.client_email,
          whatsapp_chat_history: call.whatsapp_chat_history
        },
        selectedTabIndex: 0 // Open with Activity history tab selected (index 0)
      },
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(result);
    });
  }

  /**
   * Same create-task popup the Tasks page opens, scoped to this project. The
   * dialog fetches its own project list and outcomes, so it only needs the
   * entity and the project to preselect.
   */
  openCreateTask(mode: 'ai' | 'human') {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;

    if (!entityId) {
      this.notify.showMessage('Missing entity ID', 5000);
      return;
    }

    const dialogRef = this.dialog.open(CreateTaskDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
      disableClose: true,
      data: {
        mode,
        entityId,
        projects: [],
        defaultProjectId: projectId || null,
      }
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        // Tasks created here can move leads, so pull the table and the
        // outcome counts back in step rather than leaving stale numbers.
        this.refreshConnectedLeads();
      }
    });
  }

  /**
   * Same create-task popup, scoped to one row's lead instead of an outcome.
   * Stops the click so the row doesn't also open the lead profile.
   * Optional `preset` seeds title/prompt from a signal's `task_prompt`.
   * Without a preset, prefill from the lead/job `task_prompt` when present.
   */
  openCreateTaskForLead(call: any, event?: Event, preset?: { title?: string; instructions?: string; channel?: string; mode?: 'ai' | 'human' }) {
    if (event) {
      event.stopPropagation();
    }
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = (call && call.project_id) || (this.projectDetails && this.projectDetails.project_id);

    if (!entityId || !projectId || !call || !call.people_id) {
      this.notify.showMessage('Missing lead details to create a task', 5000);
      return;
    }

    const mode = (preset && preset.mode) || 'ai';
    const leadTaskPrompt = resolveLeadTaskPrompt(call);
    const resolvedPreset = preset || (leadTaskPrompt
      ? {
          mode: 'ai' as const,
          channel: 'call',
          title: titleFromTaskPrompt(leadTaskPrompt),
          instructions: leadTaskPrompt,
        }
      : undefined);

    const dialogRef = this.dialog.open(CreateTaskDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
      disableClose: true,
      data: {
        mode,
        entityId,
        projects: [],
        defaultProjectId: projectId,
        lead: {
          peopleId: call.people_id,
          name: call.client_name || '',
          number: call.client_number && call.client_number !== 'Reveal' ? call.client_number : '',
          outcome: call.status || '',
          photoUrl: call.photo_url || '',
          taskPrompt: leadTaskPrompt || undefined,
        },
        preset: resolvedPreset,
      }
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.refreshConnectedLeads();
      }
    });
  }

  /**
   * Open signal detail (strength / reason / task_prompt for next call).
   */
  openSignalDetail(call: any, signal: PostCallSignal, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.dialog.open(SignalDetailPopupComponent, {
      width: '480px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: false,
      data: {
        signal,
        client_name: call && call.client_name,
      },
    });
  }

  /** Normalize optional BE signal payloads on a Connected row. */
  getCallSignals(call: any): PostCallSignal[] {
    return extractPostCallSignals(call);
  }

  hasCallSignals(call: any): boolean {
    return this.getCallSignals(call).length > 0;
  }

  signalStrengthClass(strength: string | undefined | null): string {
    return signalStrengthClass(strength);
  }

  /**
   * Same rule as Search tab: Pause only while the campaign is actively running.
   * Paused / completed / not-started → Start AI calling.
   */
  get isAiCallingInProgress(): boolean {
    return this.process === 'In progress';
  }

  /**
   * Copy + tone for the campaign control card shown whenever AI calling is not
   * actively running. Pure presentation over the existing workflow `process`.
   */
  get campaignStatus(): {
    tone: 'paused' | 'warn' | 'done' | 'idle';
    icon: string;
    title: string;
    subtitle: string;
    toggleLabel: string;
    toggleHint: string;
  } {
    const resumeHint = 'Opens the goal dialog so you can confirm the target before calling resumes';
    if (this.workflowPaused || this.process === 'Paused') {
      return {
        tone: 'paused',
        icon: 'pause_circle_outline',
        title: 'AI calling is paused',
        subtitle: 'No new calls are being placed. Switch it on to resume the campaign.',
        toggleLabel: 'Resume AI calling',
        toggleHint: resumeHint
      };
    }
    switch (this.process) {
      case 'Credits Insufficient':
        return {
          tone: 'warn',
          icon: 'error_outline',
          title: 'Stopped: not enough credits',
          subtitle: 'Recharge your account, then switch AI calling back on.',
          toggleLabel: 'Start again',
          toggleHint: resumeHint
        };
      case 'Leads Insufficient':
        return {
          tone: 'warn',
          icon: 'error_outline',
          title: 'Stopped: not enough leads',
          subtitle: 'Add more leads to this project, then switch AI calling back on.',
          toggleLabel: 'Start again',
          toggleHint: resumeHint
        };
      case 'Completed':
        return {
          tone: 'done',
          icon: 'check_circle_outline',
          title: 'Campaign run completed',
          subtitle: 'All selected leads have been called by the Arya agent.',
          toggleLabel: '',
          toggleHint: ''
        };
      default:
        return {
          tone: 'idle',
          icon: 'info_outline',
          title: 'AI calling is off',
          subtitle: 'Switch it on to start calling the leads in this project.',
          toggleLabel: 'Start AI calling',
          toggleHint: resumeHint
        };
    }
  }

  /** Completed runs are restarted from the header button; everything else gets the toggle. */
  get canToggleCampaign(): boolean {
    return this.process !== 'Completed';
  }

  /** Progress of the current run against its target, clamped to 0-100. */
  get goalProgressPct(): number {
    const required = Number(this.requiredLeads) || 0;
    const done = Number(this.pagination && this.pagination.totalElements) || 0;
    if (required <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, Math.round((done / required) * 100)));
  }

  /**
   * Follow-ups scheduled for this project. Prefers the project-level total; when
   * the project payload does not carry it, falls back to the rows loaded on this
   * page (see pendingFollowUpsPageOnly) so the notice never claims more than we know.
   */
  get pendingFollowUps(): number | null {
    const total = this.projectDetails && this.projectDetails.total_pending_follow_ups;
    if (typeof total === 'number') {
      return total;
    }
    if (Array.isArray(this.dataSource)) {
      const onPage = this.dataSource.filter(
        (c: any) => c && c.open_tasks && c.open_tasks.has_open_task
      ).length;
      return onPage > 0 ? onPage : null;
    }
    return null;
  }

  get pendingFollowUpsPageOnly(): boolean {
    return !(this.projectDetails && typeof this.projectDetails.total_pending_follow_ups === 'number');
  }

  /**
   * The toggle only *requests* a change: the true state comes back from the
   * project status after the goal dialog / pause call completes, so snap the
   * switch back to off and let the refreshed `process` drive the card.
   */
  onCampaignToggle(ev: MatSlideToggleChange): void {
    const wantsOn = ev.checked;
    ev.source.checked = false;
    if (wantsOn) {
      this.startAiCalling();
    }
  }

  /** A recording only exists to play when the call actually connected for some time. */
  hasRecording(call: any): boolean {
    return Number(call && call.duration) > 0;
  }

  /**
   * Start side of the Connected Start/Pause flip. Opens the same run-outreach
   * (or setup-workflow) flow Search uses when process is not In progress.
   */
  startAiCalling() {
    if (!this.projectDetails || !this.projectDetails.project_id) {
      this.notify.showMessage('Project details not available', 3000);
      return;
    }

    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    if (!entityId) {
      this.notify.showMessage('Missing entity ID', 5000);
      return;
    }

    if (this.projectDetails.final_prompt === undefined) {
      const dialogRef = this.dialog.open(WorkflowPopupComponent, {
        width: '640px',
        panelClass: 'add-edit-staff-dialog',
        disableClose: true,
        data: this.projectDetails
      });
      dialogRef.componentInstance.workflowSetup.subscribe((result) => {
        this.workflowDataService.setWorkflowData(this.projectDetails);
        const workflowType = (result && result.workflowType) ? result.workflowType : null;
        const navigationExtras = workflowType ? { queryParams: { workflowType: workflowType } } : {};
        this.router.navigate(
          [`/job/view-new-project/${this.projectDetails.project_id}/setup-workflow`],
          navigationExtras
        );
      });
      return;
    }

    const dialogRef = this.dialog.open(RunOutreachPopupComponent, {
      disableClose: true,
      width: '640px',
      data: {
        projectId: this.projectDetails.project_id,
        entityId,
        outcomes: this.projectDetails.expected_outcomes,
        useApollo: this.projectDetails.useApollo,
        workflow: this.projectDetails.workflow,
        autoScheduling: this.projectDetails.auto_scheduling,
        scheduleConfig: this.projectDetails.schedule_config,
        campaignConfig: this.projectDetails.campaign_config
      },
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      // Refresh local workflow state so the button flips back to Pause.
      this.refreshConnectedLeads();
    });
  }

  pauseWorkflow() {
    const entityId = this.profile.entity.id;
    const projectId = this.projectDetails && this.projectDetails.project_id;

    if (!entityId || !projectId) {
      this.notify.showMessage('Missing entity or project ID', 5000);
      return;
    }

    this.jobService.pauseWorkflow(entityId, projectId).subscribe(
      (res: any) => {
        this.notify.showMessage('Workflow paused successfully!', 5000);
        this.workflowPaused = true;
        this.process = 'Paused';
        if (this.projectDetails && this.projectDetails.workflow) {
          this.projectDetails.workflow.process = 'Paused';
        }
        this.stopWorkflowStatusPolling();
        this.cd.markForCheck();

        // Secondary cleanup: disable tasks for leads that already reached a
        // terminal outcome, so a paused (then possibly re-started) campaign
        // doesn't keep those leads' schedules alive. Best-effort — never
        // rolls back or blocks the pause above, which already succeeded.
        const goalOutcomes = this.projectDetails
          && this.projectDetails.campaign_config
          && Array.isArray(this.projectDetails.campaign_config.goal_outcomes)
          ? this.projectDetails.campaign_config.goal_outcomes
          : [];

        if (goalOutcomes.length) {
          this.jobService.editProjectTasks(
            entityId,
            projectId,
            'disable',
            goalOutcomes,
            'Disable tasks for terminal outcomes'
          ).subscribe(
            () => {
              // Silent success; pause is the primary action already confirmed above.
            },
            (err) => {
              console.error('editProjectTasks (disable terminal outcomes) error:', err);
              this.notify.showMessage(
                'Workflow paused, but failed to disable tasks for terminal-outcome leads',
                5000
              );
            }
          );
        }
      },
      (err) => {
        console.error('Pause workflow error:', err);

        // Extract and show backend message if available
        const backendMessage =
          err.error.message || 'Failed to pause workflow due to unknown error';

        this.notify.showMessage(backendMessage, 5000);
      }
    );
  }

  getLeadDocumentEntries(documents: Record<string, string> | null | undefined): { key: string; url: string }[] {
    if (!documents || typeof documents !== 'object') {
      return [];
    }
    return Object.keys(documents)
      .filter((k) => {
        const v = documents[k];
        return typeof v === 'string' && v.trim().length > 0;
      })
      .map((k) => ({ key: k, url: documents[k].trim() }));
  }

  formatDocumentTypeLabel(key: string): string {
    if (!key) {
      return 'Document';
    }
    return key
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  leadDocumentFilename(url: string, documentKey: string): string {
    try {
      const u = new URL(url);
      const seg = u.pathname.split('/').filter(Boolean).pop();
      if (seg && /\.[a-z0-9]{2,8}$/i.test(seg)) {
        return decodeURIComponent(seg);
      }
    } catch {
      // ignore invalid URL
    }
    const base = (documentKey || 'document').replace(/_/g, '-');
    return base;
  }

  downloadLeadDocument(url: string, documentKey: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!url || !url.trim()) {
      return;
    }
    const filename = this.leadDocumentFilename(url, documentKey);
    fetch(url, { mode: 'cors' })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Download failed');
        }
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        a.click();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      });
  }

  exportSummary() {
    const projectId = this.projectDetails && this.projectDetails.project_id;

    if (!projectId) {
      this.notify.showMessage('Project ID is missing', 3000);
      return;
    }
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.notify.showMessage('Preparing your export… this can take up to a couple of minutes for large projects.', 4000);

    const range = this.activeRange;
    this.jobService.exportConnectedLeadsExcel(
      projectId,
      this.selectedStatusFilter.length ? this.selectedStatusFilter : undefined,
      this.observedStatusFilter || undefined,
      range.startDate,
      range.endDate
    ).subscribe(
      (result) => {
        this.isExporting = false;
        this.downloadExportedFile(result.downloadUrl, result.filename);
      },
      (err) => {
        this.isExporting = false;
        this.notify.showMessage(this.getExportErrorMessage(err), 5000);
      }
    );
  }

  private getExportErrorMessage(err: any): string {
    if (err === 'EXPORT_TIMEOUT') {
      return 'Export is taking longer than expected. Please try again.';
    }
    return typeof err === 'string' ? err : 'Export failed. Please try again.';
  }

  private downloadExportedFile(downloadUrl: string, filename: string): void {
    openPresignedDownload(downloadUrl, filename);
  }

  downloadProjectWhatsAppDocuments(): void {
    const projectId = this.projectDetails && this.projectDetails.project_id;
    if (!projectId) {
      this.notify.showMessage('Project ID is missing', 3000);
      return;
    }
    if (this.isDownloadingProjectWaDocs) {
      return;
    }

    this.isDownloadingProjectWaDocs = true;
    this.notify.showMessage('Preparing WhatsApp documents zip…', 3000);

    this.jobService.downloadProjectWhatsAppDocuments(projectId).subscribe({
      next: (response) => {
        this.isDownloadingProjectWaDocs = false;
        if (!response || !response.downloadUrl) {
          this.notify.showMessage('Download URL was not returned.', 4000);
          return;
        }
        openPresignedDownload(response.downloadUrl, response.zipFilename || 'project_documents.zip');
        const count = response.fileCount != null ? response.fileCount : '';
        this.notify.showMessage(
          count !== '' ? `Download started (${count} file${count === 1 ? '' : 's'}).` : 'Download started.',
          3000
        );
      },
      error: (err) => {
        this.isDownloadingProjectWaDocs = false;
        this.notify.showMessage(getWhatsAppDocumentsErrorMessage(err), 5000);
      }
    });
  }

  downloadLeadWhatsAppDocuments(call: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const projectId =
      (call && call.project_id) ||
      (this.projectDetails && this.projectDetails.project_id);
    const clientNumber = call && call.client_number;

    if (!projectId) {
      this.notify.showMessage('Project ID is missing', 3000);
      return;
    }
    if (!clientNumber || clientNumber === 'Reveal') {
      this.notify.showMessage('Reveal the phone number before downloading WhatsApp documents.', 4000);
      return;
    }
    if (this.downloadingWaDocsClientNumber === clientNumber) {
      return;
    }

    this.downloadingWaDocsClientNumber = clientNumber;
    this.notify.showMessage('Preparing WhatsApp documents zip…', 3000);

    this.jobService.downloadProjectWhatsAppDocuments(projectId, clientNumber).subscribe({
      next: (response) => {
        this.downloadingWaDocsClientNumber = null;
        if (!response || !response.downloadUrl) {
          this.notify.showMessage('Download URL was not returned.', 4000);
          return;
        }
        openPresignedDownload(
          response.downloadUrl,
          response.zipFilename || `${clientNumber}_documents.zip`
        );
        const count = response.fileCount != null ? response.fileCount : '';
        this.notify.showMessage(
          count !== '' ? `Download started (${count} file${count === 1 ? '' : 's'}).` : 'Download started.',
          3000
        );
      },
      error: (err) => {
        this.downloadingWaDocsClientNumber = null;
        this.notify.showMessage(getWhatsAppDocumentsErrorMessage(err), 5000);
      }
    });
  }

  isDownloadingLeadWaDocs(clientNumber: string): boolean {
    return this.downloadingWaDocsClientNumber === clientNumber;
  }

  canDownloadLeadWhatsAppDocuments(call: any): boolean {
    return !!(call && call.client_number && call.client_number !== 'Reveal');
  }

  buildCallHistoryFilters(): {
    status?: string; name?: string; mobile?: string; duration?: string;
    observed_status?: string; reviewed_by_human?: boolean;
    start_date?: string; end_date?: string;
    date_basis?: 'latest_contact' | 'lead_added';
    signal_strength?: string;
  } | undefined {
    const filters: {
      status?: string; name?: string; mobile?: string; duration?: string;
      observed_status?: string; reviewed_by_human?: boolean;
      start_date?: string; end_date?: string;
      date_basis?: 'latest_contact' | 'lead_added';
      signal_strength?: string;
    } = {};

    if (this.reviewedByHumanFilter) {
      filters.reviewed_by_human = true;
    } else if (this.observedStatusFilter) {
      filters.observed_status = this.observedStatusFilter;
    } else if (this.selectedStatusFilter.length > 0) {
      filters.status = this.selectedStatusFilter.join(',');
    }
    if (this.selectedSignalStrengthFilter.length > 0) {
      filters.signal_strength = this.selectedSignalStrengthFilter.join(',');
    }
    if (this.nameFilter && this.nameFilter.trim()) {
      filters.name = this.nameFilter.trim();
    }
    if (this.mobileFilter && this.mobileFilter.trim()) {
      filters.mobile = this.mobileFilter.trim();
    }
    if (this.durationFilter && this.durationFilter.trim()) {
      filters.duration = this.durationFilter.trim();
    }
    const range = this.activeRange;
    if (range.startDate && range.endDate) {
      filters.start_date = range.startDate;
      filters.end_date = range.endDate;
      if (this.selectedDateBasis === 'lead_added') {
        filters.date_basis = 'lead_added';
      }
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  hasActiveCallHistoryFilters(): boolean {
    return !!(
      this.reviewedByHumanFilter ||
      this.selectedStatusFilter.length > 0 ||
      this.selectedSignalStrengthFilter.length > 0 ||
      this.observedStatusFilter ||
      (this.nameFilter && this.nameFilter.trim()) ||
      (this.mobileFilter && this.mobileFilter.trim()) ||
      (this.durationFilter && this.durationFilter.trim())
    );
  }

  closeAllColumnFilters() {
    this.activeFilterPanel = null;
    this.filterPanelPosition = {};
  }

  private positionFilterPanel(event: MouseEvent, alignRight = false) {
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const width = this.filterPanelWidth;
    let left = alignRight ? rect.right - width : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    this.filterPanelPosition = {
      position: 'fixed',
      top: `${rect.top - 8}px`,
      left: `${left}px`,
      width: `${width}px`,
      transform: 'translateY(-100%)',
      zIndex: '10000',
    };
  }

  private openFilterPanel(
    panel: 'name' | 'mobile' | 'duration' | 'status' | 'signal',
    event: MouseEvent,
    alignRight = false
  ) {
    if (this.activeFilterPanel === panel) {
      this.closeAllColumnFilters();
      return;
    }

    this.closeAllColumnFilters();
    this.activeFilterPanel = panel;

    if (panel === 'name') {
      this.nameFilterDraft = this.nameFilter;
    } else if (panel === 'mobile') {
      this.mobileFilterDraft = this.mobileFilter;
    } else if (panel === 'duration') {
      this.durationFilterDraft = this.durationFilter;
    }

    this.positionFilterPanel(event, alignRight);
  }

  private normalizeFilterValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  hasFilterDraftValue(draft: string | number | null | undefined): boolean {
    return this.normalizeFilterValue(draft).length > 0;
  }

  // Status filter methods
  toggleStatusFilter(event: MouseEvent) {
    this.openFilterPanel('status', event, true);
  }

  onStatusFilterChange(status: string) {
    const index = this.selectedStatusFilter.indexOf(status);
    if (index > -1) {
      this.selectedStatusFilter.splice(index, 1);
    } else {
      this.selectedStatusFilter.push(status);
    }
  }

  applyStatusFilter() {
    this.closeAllColumnFilters();
    this.pageIndex = 1; // Reset to first page when applying filter
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  clearStatusFilter() {
    this.selectedStatusFilter = [];
    this.observedStatusFilter = null;
    this.reviewedByHumanFilter = false;
    this.closeAllColumnFilters();
    this.pageIndex = 1; // Reset to first page when clearing filter
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatusFilter.includes(status);
  }

  toggleSignalStrengthFilter(event: MouseEvent) {
    this.openFilterPanel('signal', event, true);
  }

  onSignalStrengthFilterChange(strength: SignalStrength) {
    const index = this.selectedSignalStrengthFilter.indexOf(strength);
    if (index > -1) {
      this.selectedSignalStrengthFilter.splice(index, 1);
    } else {
      this.selectedSignalStrengthFilter.push(strength);
    }
  }

  applySignalStrengthFilter() {
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  clearSignalStrengthFilter() {
    this.selectedSignalStrengthFilter = [];
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  isSignalStrengthSelected(strength: SignalStrength): boolean {
    return this.selectedSignalStrengthFilter.includes(strength);
  }

  // Returns true when the outcome card represents the only active status filter
  isOutcomeCardActive(status: string): boolean {
    return this.selectedStatusFilter.length === 1 && this.selectedStatusFilter[0] === status;
  }

  // Clicking an outcome card applies the same status filter as the "Filter by
  // outcome" column option. Passing null (Total card) clears the outcome filter,
  // and clicking an already-active card toggles it off.
  onOutcomeCardClick(status: string | null) {
    const hadObserved = !!this.observedStatusFilter;
    this.observedStatusFilter = null;
    this.reviewedByHumanFilter = false;
    if (!status) {
      if (this.selectedStatusFilter.length === 0 && !hadObserved) {
        return;
      }
      this.selectedStatusFilter = [];
    } else if (!hadObserved && this.isOutcomeCardActive(status)) {
      this.selectedStatusFilter = [];
    } else {
      this.selectedStatusFilter = [status];
    }
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
    this.fetchLeadsStatusCount();
  }

  isReviewedByHumanCardActive(): boolean {
    return this.reviewedByHumanFilter;
  }

  onReviewedByHumanCardClick() {
    if (this.reviewedByHumanFilter) {
      this.reviewedByHumanFilter = false;
    } else {
      this.reviewedByHumanFilter = true;
      this.selectedStatusFilter = [];
      this.observedStatusFilter = null;
    }
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
    this.fetchLeadsStatusCount();
  }

  getReviewedByHumanCount(): number {
    return this.reviewedByHumanTotalCount;
  }

  // ----- Timeframe filter -----

  /**
   * Resolve the selected timeframe to a concrete IST YYYY-MM-DD range, or an
   * empty range for all-time. Mirrors the Reports page so a day means the same
   * thing on both screens.
   */
  private resolveDateRange(): DateRange {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = `${d.getMonth() + 1}`.padStart(2, '0');
      const day = `${d.getDate()}`.padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const today = new Date();

    switch (this.selectedTimeframe) {
      case 'today':
        return { startDate: fmt(today), endDate: fmt(today) };
      case 'week': {
        const start = new Date(today);
        // Week starting Monday.
        start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
        return { startDate: fmt(start), endDate: fmt(today) };
      }
      case 'month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: fmt(start), endDate: fmt(today) };
      }
      case 'last30': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { startDate: fmt(start), endDate: fmt(today) };
      }
      case 'custom':
        // A half-filled custom range is not a range: the API rejects one date
        // without the other, so hold at all-time until both are set.
        if (this.customStartDate && this.customEndDate) {
          return { startDate: this.customStartDate, endDate: this.customEndDate };
        }
        return {};
      default:
        return {};
    }
  }

  /**
   * The window in force, held as fields rather than getters: the template reads
   * all three, and a getter would re-resolve the dates on every change-detection
   * pass over a table that can be hundreds of rows.
   */
  private syncActiveRange() {
    this.activeRange = this.resolveDateRange();
    this.hasDateFilter = !!(this.activeRange.startDate && this.activeRange.endDate);
    this.activeRangeLabel = this.buildRangeLabel();
  }

  /**
   * Names the window the credits figure covers. Without it the number silently
   * changes meaning when the filter moves and reads as an unexplained drop.
   */
  private buildRangeLabel(): string {
    const { startDate, endDate } = this.activeRange;
    if (!startDate || !endDate) {
      return 'All time';
    }
    if (this.selectedTimeframe !== 'custom') {
      const option = this.timeframeOptions.find(o => o.value === this.selectedTimeframe);
      return option ? option.label : 'All time';
    }
    const pretty = (iso: string) => {
      const [y, m, d] = iso.split('-');
      return `${Number(d)} ${this.getMonthAbbreviation(Number(m) - 1)} ${y}`;
    };
    return startDate === endDate
      ? pretty(startDate)
      : `${pretty(startDate)} – ${pretty(endDate)}`;
  }

  onTimeframeChange() {
    if (this.selectedTimeframe !== 'custom') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.onDateFilterChange();
  }

  /**
   * Reload everything the window scopes. The table, the outcome cards and the
   * credits all have to move together, or the page shows one period's rows
   * under another period's totals.
   */
  onDateFilterChange() {
    if (this.selectedTimeframe === 'custom'
      && ((this.customStartDate ? 1 : 0) + (this.customEndDate ? 1 : 0)) === 1) {
      // Half a custom range: wait for the other date rather than firing a
      // request that would come back as all-time and look like a no-op.
      return;
    }
    this.syncActiveRange();
    // Neither "ever held this status" nor the human-review flag is answerable
    // inside a window, so their cards come off the strip — drop the filters with
    // them rather than leave the table narrowed by a control that is gone.
    if (this.hasDateFilter) {
      this.observedStatusFilter = null;
      this.reviewedByHumanFilter = false;
    }
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
    this.fetchLeadsStatusCount();
    this.fetchUsage();
  }

  /**
   * date_basis only affects the two endpoints below (not usage/credits), so
   * this reloads just the table and the outcome cards rather than reusing
   * onDateFilterChange's full reload.
   */
  onDateBasisChange() {
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
    this.fetchLeadsStatusCount();
  }

  /**
   * Credits and talk time over the active window, from the same precomputed
   * day rollups the Reports page reads.
   *
   * Deliberately not narrowed by the outcome, name or mobile filters: this is
   * what the project spent, not what the current view spent. Summing the listed
   * rows instead would look filter-aware but be wrong, because the listing walk
   * stops early on large projects and holds only part of them.
   */
  fetchUsage() {
    const projectId = this.projectId || (this.projectDetails && this.projectDetails.project_id);
    if (!projectId) {
      return;
    }
    const range = this.activeRange;
    this.usageLoading = true;
    this.reportsService.getReportMetrics({
      projectId,
      startDate: range.startDate,
      endDate: range.endDate,
    }).subscribe((res) => {
      this.usageLoading = false;
      const metrics = (res && res.metrics) || ({} as any);
      // Null means the rows in scope predate the credit counter, which is not
      // the same answer as zero — show a dash rather than a spent-nothing claim.
      this.creditsUsed = typeof metrics.total_credits === 'number' ? metrics.total_credits : null;
      // Billed and actual are both shown because they answer different
      // questions: billed is what the credits were spent on, actual is what was
      // spent talking. Billed runs ahead by the client's pulse, so showing only
      // one of them makes the credit figure look wrong against it.
      this.billedMinutes = typeof metrics.billed_minutes === 'number' ? metrics.billed_minutes : null;
      this.billedMinutesApprox = metrics.billed_minutes_approx === true;
      this.minutesCalled = typeof metrics.actual_minutes === 'number' ? metrics.actual_minutes : null;
      this.cd.markForCheck();
    }, (err) => {
      this.usageLoading = false;
      this.creditsUsed = null;
      this.billedMinutes = null;
      this.billedMinutesApprox = false;
      this.minutesCalled = null;
      console.error('Error fetching project usage:', err);
      this.cd.markForCheck();
    });
  }

  toggleNameFilter(event: MouseEvent) {
    this.openFilterPanel('name', event);
  }

  applyNameFilter() {
    this.nameFilter = this.normalizeFilterValue(this.nameFilterDraft);
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  clearNameFilter() {
    this.nameFilter = '';
    this.nameFilterDraft = '';
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  toggleMobileFilter(event: MouseEvent) {
    this.openFilterPanel('mobile', event);
  }

  applyMobileFilter() {
    this.mobileFilter = this.normalizeFilterValue(this.mobileFilterDraft);
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  clearMobileFilter() {
    this.mobileFilter = '';
    this.mobileFilterDraft = '';
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  toggleDurationFilter(event: MouseEvent) {
    this.openFilterPanel('duration', event);
  }

  applyDurationFilter() {
    this.durationFilter = this.normalizeFilterValue(this.durationFilterDraft);
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  clearDurationFilter() {
    this.durationFilter = '';
    this.durationFilterDraft = '';
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-btn') && !target.closest('.column-filter-panel')) {
      this.closeAllColumnFilters();
    }
    if (!target.closest('.outcome-info-btn') && !target.closest('.human-review-panel')) {
      this.closeHumanReviewPanel();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.activeFilterPanel) {
      this.closeAllColumnFilters();
    }
    if (this.humanReviewPanelCall) {
      this.closeHumanReviewPanel();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.activeFilterPanel) {
      this.closeAllColumnFilters();
    }
    if (this.humanReviewPanelCall) {
      this.closeHumanReviewPanel();
    }
  }

  fetchLeadsStatusCount() {
    if (!this.projectDetails || !this.projectDetails.project_id) {
      return;
    }
    // The cards are the project overview and double as the filter buttons, so
    // they must not shrink when one of them is selected: never scope this call
    // by reviewed_by_human. The Reviewed by human filter only narrows the table.
    const range = this.activeRange;
    const dateBasis = (range.startDate && range.endDate && this.selectedDateBasis === 'lead_added')
      ? this.selectedDateBasis : undefined;
    this.jobService.fetchLeadsStatusCount(
      this.projectDetails.project_id, undefined, range.startDate, range.endDate, dateBasis
    ).subscribe((res: any) => {
      this.leadsStatusCount = res;
      if (res && typeof res.reviewed_by_human_count === 'number') {
        this.reviewedByHumanTotalCount = res.reviewed_by_human_count;
      }
      this.cd.markForCheck();
    }, (err) => {
      console.error('Error fetching leads status count:', err);
    });
  }

  getStatusKeys() {
    const counts = this.leadsStatusCount && this.leadsStatusCount.status_counts;
    if (!counts) {
      return [];
    }
    const statuses = Object.keys(counts);
    // Unique Link Clicks is a superset of the "Link Clicked" outcome, so the
    // outcome card is redundant next to it — but only drop it while that card
    // is actually on screen, or a project whose rollup has no observed counts
    // would be left with no click number at all.
    if (this.uniqueLinkClicks == null) {
      return statuses;
    }
    return statuses.filter(status => !isLinkClickedStatus(status));
  }

  getStatusCount(status: string) {
    if (this.leadsStatusCount && this.leadsStatusCount.status_counts && this.leadsStatusCount.status_counts[status]) {
      return this.leadsStatusCount.status_counts[status];
    }
    return 0;
  }

  /**
   * Distinct leads that have opened a campaign link at least once.
   *
   * Two things separate this from the "Link Clicked" outcome card. It counts a
   * lead once however many times it clicked, and it keeps counting a lead after
   * it moves on — a lead that clicked and was then called again sits at the
   * newer status and drops out of that card, but not out of this.
   *
   * Null when the API omits observed_status_counts: the project's history has
   * not been folded into the rollup yet, and a partial number shown here would
   * read as a total.
   */
  get uniqueLinkClicks(): number | null {
    const observed = this.leadsStatusCount && this.leadsStatusCount.observed_status_counts;
    const key = this.linkClickedObservedKey;
    if (!key) {
      return null;
    }
    const count = observed[key];
    return typeof count === 'number' ? count : null;
  }

  /** This project's own spelling of the link-clicked outcome, as the API keys it. */
  private get linkClickedObservedKey(): string | null {
    const observed = this.leadsStatusCount && this.leadsStatusCount.observed_status_counts;
    if (!observed) {
      return null;
    }
    return Object.keys(observed).find(key => isLinkClickedStatus(key)) || null;
  }

  get uniqueLinkClicksTooltip(): string {
    return 'Distinct leads that clicked at least once, including those since moved on.';
  }

  isUniqueLinkClicksCardActive(): boolean {
    return !!this.observedStatusFilter && isLinkClickedStatus(this.observedStatusFilter);
  }

  /**
   * Filters the table to every lead that has ever clicked, which is a different
   * set from the "Link Clicked" outcome card: leads called again since sit at a
   * later status and are absent from that one. Toggles off when re-clicked.
   */
  onUniqueLinkClicksCardClick() {
    this.observedStatusFilter = this.isUniqueLinkClicksCardActive()
      ? null
      : this.linkClickedObservedKey;
    this.selectedStatusFilter = [];
    this.reviewedByHumanFilter = false;
    this.closeAllColumnFilters();
    this.pageIndex = 1;
    this.getDBCallsHistory(this.pageSize, this.pageIndex);
  }

  ensureActivityStream() {
    this.showActivityStream = true;
    this.syncActivityStream();
  }

  syncActivityStream() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const projectId = this.projectId || (this.projectDetails && this.projectDetails.project_id);
    if (!entityId || !projectId) {
      return;
    }
    if (this.streamSub) {
      return;
    }

    this.outreachActivityService.connect(projectId, entityId);
    this.streamSub = this.outreachActivityService.events$.subscribe((events) => {
      this.streamEvents = events;
      if (!this.streamPaused) {
        this.scrollActivityLogToBottom();
      }
      this.cd.markForCheck();
    });
    this.wsConnectedSub = this.outreachActivityService.connected$.subscribe((connected) => {
      this.wsConnected = connected;
      this.cd.markForCheck();
    });
  }

  teardownActivityStream() {
    if (this.streamSub) {
      this.streamSub.unsubscribe();
      this.streamSub = null;
    }
    if (this.wsConnectedSub) {
      this.wsConnectedSub.unsubscribe();
      this.wsConnectedSub = null;
    }
    this.outreachActivityService.teardown();
    this.streamEvents = [];
    this.wsConnected = false;
    this.streamPaused = false;
    this.showActivityStream = false;
  }

  getDisplayStreamEvents(): OutreachActivityEvent[] {
    if (this.streamPaused) {
      return this.streamEvents.slice(-this.compactLogLimit);
    }
    return this.streamEvents.slice(-this.compactLogLimit);
  }

  toggleStreamPause() {
    this.streamPaused = !this.streamPaused;
  }

  resumeStream() {
    this.streamPaused = false;
    this.scrollActivityLogToBottom();
  }

  getStreamStatusText(): string {
    if (this.streamPaused) {
      return 'Paused';
    }
    if (this.wsConnected) {
      return 'Live';
    }
    return 'Connecting…';
  }

  private scrollActivityLogToBottom() {
    setTimeout(() => {
      if (this.activityLogScroll && this.activityLogScroll.nativeElement) {
        const el = this.activityLogScroll.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }

  // TrackBy function for better rendering performance
  trackByCallId(index: number, call: any): any {
    return (call && call.call_sid) || (call && call.people_id) || index;
  }

  trackByStatus(index: number, status: string): string {
    return status;
  }

  hasHumanReview(call: any): boolean {
    if (!call) {
      return false;
    }
    return call.reviewed_by_human === true
      || call.reviewed_by_human === 'true'
      || !!(call.human_reviewed_by || call.human_comment);
  }

  getHumanReviewRemark(call: any): string {
    return String((call && call.human_comment) || '').trim();
  }

  getHumanReviewEditor(call: any): string {
    return String((call && call.human_reviewed_by) || '').trim();
  }

  getHumanReviewFromOutcome(call: any): string {
    return String((call && call.ai_outcome) || '').trim();
  }

  getHumanReviewToOutcome(call: any): string {
    return String((call && call.status) || '').trim();
  }

  hasHumanReviewOutcomeChange(call: any): boolean {
    const from = this.getHumanReviewFromOutcome(call);
    const to = this.getHumanReviewToOutcome(call);
    return from && to && from !== to;
  }

  /**
   * Same green "Approved" stamp call-summary-popup shows (its
   * showAddInOtherProjectStamp getter / .approved-stamp markup): the outcome
   * asked to push this lead into another project, and a human has signed
   * off on it.
   */
  hasApprovedOutcomeChange(call: any): boolean {
    return !!(call && call.add_in_other_project_approved === true);
  }

  formatHumanReviewAt(call: any): string | null {
    const raw = call && call.human_reviewed_at;
    if (raw == null || raw === '') {
      return null;
    }
    const seconds = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return null;
    }
    const date = new Date(seconds < 1e12 ? seconds * 1000 : seconds);
    if (isNaN(date.getTime())) {
      return null;
    }
    const day = date.getDate();
    const month = this.getMonthAbbreviation(date.getMonth());
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = this.padNumber(minutes);
    return `${month} ${day}, ${year}, ${formattedHours}:${formattedMinutes} ${period}`;
  }

  formatHumanReviewAudit(call: any): string | null {
    const editor = this.getHumanReviewEditor(call);
    const when = this.formatHumanReviewAt(call);
    if (editor && when) {
      return `By ${editor} · ${when}`;
    }
    if (editor) {
      return `By ${editor}`;
    }
    if (when) {
      return when;
    }
    return null;
  }

  openHumanReviewPanel(call: any, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (
      this.humanReviewPanelCall &&
      call &&
      this.humanReviewPanelCall.job_id === call.job_id
    ) {
      this.closeHumanReviewPanel();
      return;
    }
    this.humanReviewPanelCall = call;
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const panelWidth = 280;
    let left = rect.left - panelWidth + rect.width;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
    this.humanReviewPanelPosition = {
      position: 'fixed',
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
      width: `${panelWidth}px`,
      zIndex: '10000',
    };
    this.cd.markForCheck();
  }

  closeHumanReviewPanel() {
    this.humanReviewPanelCall = null;
    this.humanReviewPanelPosition = {};
  }

}

